import { supabase } from "@/integrations/supabase/client";
import { buildPersistedSessionFields } from "@/lib/sessionResult";
import { countUserTurns } from "@/lib/sessionAnalysis";
import { scoreVoiceSession, type VoiceTurn } from "./voiceScore";

interface PersistVoiceSessionInput {
  situationId?: string;
  catalogSlug?: string;
  startedAt: number;
  transcripts: Array<{ role: "user" | "assistant"; text: string; timestamp: Date }>;
  vocabulary?: Array<{ word: string; translation: string }> | null;
}

export type PersistVoiceSessionResult = {
  sessionId: string | null;
  tooShort: boolean;
  userTurns: number;
};

export async function persistVoiceSession({
  situationId,
  catalogSlug,
  startedAt,
  transcripts,
  vocabulary,
}: PersistVoiceSessionInput): Promise<PersistVoiceSessionResult> {
  const empty = { sessionId: null, tooShort: true, userTurns: 0 };
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return empty;

  const durationSeconds = Math.max(0, Math.round((Date.now() - startedAt) / 1000));
  const turns: VoiceTurn[] = transcripts
    .map((item) => ({
      role: item.role,
      text: item.text.replace(/\[final\]/g, "").trim(),
    }))
    .filter((item) => item.text);

  if (durationSeconds < 2 && turns.length === 0) {
    return empty;
  }

  const userTurns = countUserTurns(turns);
  const persisted = buildPersistedSessionFields(catalogSlug, userTurns);
  const tooShort = persisted.tooShort;
  const scores = scoreVoiceSession(turns, vocabulary, durationSeconds);
  const startedIso = new Date(startedAt).toISOString();
  const endedIso = new Date().toISOString();

  const transcriptPayload = transcripts.map((item) => ({
    role: item.role,
    text: item.text.replace(/\[final\]/g, "").trim(),
    timestamp: item.timestamp.toISOString(),
  }));

  const baseRow = {
    user_id: user.id,
    situation_id: situationId || null,
    started_at: startedIso,
    ended_at: endedIso,
    duration_seconds: durationSeconds,
    transcript: transcriptPayload,
  };

  const withCatalog = {
    ...baseRow,
    catalog_slug: catalogSlug || null,
    status: persisted.status,
    analysis: persisted.analysis,
  };

  let session = (await supabase
    .from("conversation_sessions")
    .insert(withCatalog)
    .select("id")
    .single()).data;

  if (!session) {
    const fallback = await supabase
      .from("conversation_sessions")
      .insert(baseRow)
      .select("id")
      .single();
    session = fallback.data;
    if (fallback.error) {
      console.error("Failed to persist voice session", fallback.error);
    }
  }

  const { error: scoreError } = await supabase
    .from("conversation_scores")
    .insert({
      user_id: user.id,
      conversation_type: "voice",
      situation_id: situationId || null,
      session_id: session?.id ?? null,
      duration_seconds: durationSeconds,
      ...scores,
    });

  if (scoreError) {
    console.error("Failed to persist voice score", scoreError);
  }

  const billedMinutes = Math.max(1, Math.ceil(durationSeconds / 60));
  const { error: settleError } = await supabase.rpc("settle_voice_minutes", {
    minutes: billedMinutes,
  });
  if (settleError) {
    console.error("Failed to settle voice minutes", settleError);
  }

  return {
    sessionId: session?.id ?? null,
    tooShort,
    userTurns,
  };
}
